import type { CSSObject, Theme } from "@mui/material/styles";

/**
 * A row of action buttons that wraps only when the next button will not fit (#5815).
 *
 * The proposal and work agreement rows each used to pick a pane width below which they stopped
 * being a row: a proposal below a 600px pane, a work agreement below a 900px one. Neither width was
 * the width at which a second button stopped fitting. A button needs 192px, so an 850px pane stacked
 * Revise and End Agreement full width with room for four buttons beside each other, and a 590px pane
 * stacked a proposal's actions with room for three (#5240 had decided that stacking was correct).
 *
 * So the row asks the one question that matters — does the next button fit? — and the grid answers
 * it. Each track is at least `actionButtonMinimum` and shares out what is left equally, so the
 * buttons on a row are the same width. `auto-fill` rather than `auto-fit`, so the tracks a short row
 * did not fill stay standing and a lone button keeps its width instead of taking the whole row. The
 * `min(100%, …)` lets the only track shrink with a pane narrower than one button, rather than
 * overflow it.
 */
export function actionButtonMinimum(theme: Theme): number {
  return parseFloat(theme.spacing(24));
}

export function actionRowTracks(theme: Theme): string {
  return `repeat(auto-fill, minmax(min(100%, ${actionButtonMinimum(theme)}px), 1fr))`;
}

/**
 * Named so that a button in the row can ask how wide the row is, rather than how wide the pane is.
 *
 * A container cannot query itself, but its children can, and a child is what needs the answer: a
 * quiet action (#5240) hugs its label, and in a row of one track a full-width button is what reads
 * correctly, so the hug has to be lifted exactly when the row seats a single button. The pane's
 * width cannot say when that is without knowing every gutter and padding between the pane and the
 * row, and those differ by page and by band.
 */
export const ACTION_ROW_CONTAINER = "action-row";

export const actionRowContainment: CSSObject = {
  containerName: ACTION_ROW_CONTAINER,
  containerType: "inline-size",
};

/**
 * True while the row is too narrow for two buttons and the gap between them — the only width at
 * which the row is one stacked column. `gap` is the row's `column-gap` in pixels.
 */
export function actionRowSeatsOneButton(theme: Theme, gap: number): string {
  const twoButtons = 2 * actionButtonMinimum(theme) + gap;
  // Container query ranges are inclusive, so the width that seats exactly two is left out.
  return `@container ${ACTION_ROW_CONTAINER} (max-width: ${twoButtons - 0.05}px)`;
}
